import { useSelector } from 'react-redux'
import { useQuery } from 'react-query'
import axios from '../../utils/axios'
import { API_BASE_URL } from '../../constants'
import { equals } from 'ramda'
import { useDebounce } from 'use-debounce'

const translateApiFiltersToBackend = (apiFilters) => {
  return Object.values(apiFilters)
    .filter((filter) => filter.enabled && !equals(filter.initialValue, filter.value))
    .map((filter) => ({
      name: filter.backendName,
      value: filter.backendValueTransformer(filter.value),
    }))
}

export { translateApiFiltersToBackend }

// The table page leaves out 'Variant Details' (~93 KB a row): no table cell,
// popover, bookmark or report reads it from the page. The details dialog reads
// its own endpoint, a bookmark's pinned copy is filled by the server, the report
// reads the stored details, and downloads always carry every column. The answer
// names the column in `omitted_columns`; a server without the option ignores it
// and serves the full page, which reads the same.
export const OMIT_VARIANT_DETAILS = { omit_variant_details: true }

export const fetchVariants = async (
  fileId,
  sampleName,
  page,
  pageSize,
  sortBy,
  sortDirection,
  filters,
  trio,
  tableId,
) => {
  let trioData = trio
  const updatedFilters = filters.filter((filter) => {
    if (filter.name === 'TrioAnalysisRowFilter') {
      trioData = filter.value.value
      return false
    }
    return true
  })

  const url = sampleName ? `${API_BASE_URL}/variants/${fileId}/${sampleName}` : `${API_BASE_URL}/variants/${fileId}`

  const { data } = await axios.post(url, {
    page: page + 1,
    page_size: pageSize,
    sort_by: sortBy,
    sort_direction: sortDirection,
    filters: updatedFilters,
    trio: trioData,
    table_id: tableId,
    ...OMIT_VARIANT_DETAILS,
  })

  return data
}

export const useVariants = ({
  fileId,
  sampleName,
  page = 0,
  pageSize = 10,
  sortBy = 0,
  sortDirection = 'asc',
  trio = null,
  tableId = null,
}) => {
  const apiFilters = useSelector((state) => state)
  const translatedFilters = translateApiFiltersToBackend(apiFilters)
  const [debouncedApiFilters, _] = useDebounce(translatedFilters, 1000, { equalityFn: equals })

  return useQuery(
    ['variants', fileId, sampleName, page, pageSize, sortBy, sortDirection, debouncedApiFilters, trio, tableId],
    () => fetchVariants(fileId, sampleName, page, pageSize, sortBy, sortDirection, debouncedApiFilters, trio, tableId),
    { enabled: !!fileId && !!tableId },
  )
}

export const downloadVariants = async (
  fileId,
  sampleName,
  format,
  { page, pageSize, sortBy, sortDirection, filters, trio, tableId },
) => {
  let trioData = trio
  const updatedFilters = filters.filter((filter) => {
    if (filter.name === 'TrioAnalysisRowFilter') {
      trioData = filter.value.value
      return false
    }
    return true
  })

  const baseUrl = sampleName ? `${API_BASE_URL}/variants/${fileId}/${sampleName}` : `${API_BASE_URL}/variants/${fileId}`

  const response = await axios.post(
    `${baseUrl}?download=${format}`,
    {
      page: page + 1,
      page_size: pageSize,
      sort_by: sortBy,
      sort_direction: sortDirection,
      filters: updatedFilters,
      trio: trioData,
      table_id: tableId,
    },
    {
      responseType: 'blob',
    },
  )

  // Get current date in YYYY-MM-DD format
  const today = new Date().toISOString().split('T')[0]

  // Create filename with sample name and date
  const filename = `${sampleName || 'variants'}-${today}.zip`

  const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }))
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

export const useVariantsDownload = () => {
  const apiFilters = useSelector((state) => state)
  const translatedFilters = translateApiFiltersToBackend(apiFilters)
  const [debouncedApiFilters] = useDebounce(translatedFilters, 1000, { equalityFn: equals })

  const handleDownload = (fileId, sampleName, format, { page, pageSize, sortBy, sortDirection, trio, tableId }) => {
    return downloadVariants(fileId, sampleName, format, {
      page,
      pageSize,
      sortBy,
      sortDirection,
      filters: debouncedApiFilters,
      trio,
      tableId,
    })
  }

  return { handleDownload }
}

export const fetchMultisampleVariants = async (
  analysisWrapperId,
  page,
  pageSize,
  sortBy,
  sortDirection,
  filters,
  tableId,
) => {
  const { data } = await axios.post(`${API_BASE_URL}/multisample-variants/${analysisWrapperId}`, {
    page: page + 1,
    page_size: pageSize,
    sort_by: sortBy,
    sort_direction: sortDirection,
    filters,
    table_id: tableId,
    ...OMIT_VARIANT_DETAILS,
  })
  return data
}

export const useMultisampleVariants = ({
  analysisWrapperId,
  page = 0,
  pageSize = 10,
  sortBy = 0,
  sortDirection = 'asc',
  tableId = null,
}) => {
  const apiFilters = useSelector((state) => state)
  const translatedFilters = translateApiFiltersToBackend(apiFilters)
  const [debouncedApiFilters] = useDebounce(translatedFilters, 1000, { equalityFn: equals })

  return useQuery(
    ['multisample-variants', analysisWrapperId, page, pageSize, sortBy, sortDirection, debouncedApiFilters, tableId],
    () =>
      fetchMultisampleVariants(analysisWrapperId, page, pageSize, sortBy, sortDirection, debouncedApiFilters, tableId),
    { enabled: !!analysisWrapperId && !!tableId },
  )
}
